import { useContext, useState } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import Loading from "../../Loading";
import AllocationBar from "../../components/AllocationBar/AllocationBar";
import PerformancePeriodSelector from "../../components/PerformancePeriodSelector";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { getRiskAllocation } from "../../rebalances/lib";
import {
  PerformanceSelection,
  figurePeriodParams,
  isChained,
  useQueryPerformanceFigure,
} from "../performanceFigure";
import { HouseholdContext } from "./HouseholdInfo";
import {
  HouseholdWithAccountsAndRiskAllocation,
  useQueryHouseholdPerformance,
} from "./lib";

const HouseholdPerformance = ({
  rowSelection = {},
  useall = false,
  household: propHousehold,
}: {
  rowSelection?: Record<string, boolean>;
  useall?: boolean;
  household?: HouseholdWithAccountsAndRiskAllocation;
}) => {
  const { household: contextHousehold } = useContext(HouseholdContext);
  const household = propHousehold ?? contextHousehold;
  const householdId = household?.id;

  const [customPeriodParams, setCustomPeriodParams] = useState("");
  const [selection, setSelection] = useState<PerformanceSelection>();

  // The accounts the panel reads: all of them, or the Portfolio tab's picks.
  const isAllAccounts =
    useall ||
    Object.keys(rowSelection).length === (household?.accounts ?? []).length;
  const selectedAccountIds = isAllAccounts
    ? undefined
    : (household?.accounts ?? [])
        .filter((account) => rowSelection[account.id] === true)
        .map((account) => account.id);

  // The Performance figure (APP-2005): the return of the selected accounts
  // over the period, not a balance-weighted sum of the account returns.
  const {
    figure,
    isPending: isPendingFigure,
    isError: isErrorFigure,
  } = useQueryPerformanceFigure(
    "households",
    householdId,
    selection,
    // No selected account has no figure to ask for.
    selectedAccountIds?.length === 0 ? undefined : selectedAccountIds,
  );

  // Every page of `GET /households/:id/performance` (APP-1653), over the
  // dates of the figure response (R83).
  const {
    data: householdAccountPerformance,
    isPending: isPendingHouseholdAccountPerformance,
    isError: isErrorHouseholdAccountPerformance,
  } = useQueryHouseholdPerformance(
    householdId,
    figurePeriodParams(figure, customPeriodParams),
    { enabled: !isPendingFigure || selectedAccountIds?.length === 0 },
  );

  // The user can filter out accounts from this calculation, so we need to
  // calculate these totals in the frontend for the selected accounts only.
  const accountsData =
    useall ||
    Object.keys(rowSelection).length === (household?.accounts ?? []).length
      ? (household?.accounts ?? [])
      : (household?.accounts ?? []).filter(
          (account) => rowSelection[account.id] === true,
        );
  const householdTotalsCurrent = accountsData.reduce(
    (sums, account) => ({
      aum: sums.aum + (account.accountBalance ?? 0),
      cash: sums.cash + (account.cashBalance ?? 0),
    }),
    {
      aum: 0,
      cash: 0,
    },
  );

  // The user can filter out accounts from this calculation, so we need to
  // calculate these totals in the frontend for the selected accounts only.
  const householdAccountPerformanceData =
    useall ||
    Object.keys(rowSelection).length === (household?.accounts ?? []).length
      ? (householdAccountPerformance ?? [])
      : (householdAccountPerformance ?? []).filter(
          (account) =>
            typeof account.accountId !== "undefined" &&
            rowSelection[account.accountId] === true,
        );

  const householdPerformance = householdAccountPerformanceData.reduce(
    (sums, account) => ({
      startingBalance:
        (sums.startingBalance ?? 0) + (account.startingBalance ?? 0),
      balance: sums.balance + account.balance,
      income: sums.income + account.income,
      contributions: sums.contributions + account.contributions,
      withdrawals: sums.withdrawals + account.withdrawals,
      performanceIncome: sums.performanceIncome + account.performanceIncome,
    }),
    {
      startingBalance: 0,
      balance: 0,
      income: 0,
      contributions: 0,
      withdrawals: 0,
      performanceIncome: 0,
    },
  );

  return (
    <Row className="mb-2">
      <Col>
        <Row>
          <Col as="dt" xxl={5} xl={6} lg={3} md={4}>
            Risk Allocation
          </Col>
          <Col as="dd">
            <AllocationBar
              data={household?.riskAllocation?.current}
              converter={getRiskAllocation}
            />
          </Col>
        </Row>
        <PerformancePeriodSelector
          setCustomPeriodParams={setCustomPeriodParams}
          onSelectionChange={setSelection}
        />
        {isPendingHouseholdAccountPerformance ||
        (isPendingFigure && selectedAccountIds?.length !== 0) ? (
          <Loading message="Performance" />
        ) : isErrorHouseholdAccountPerformance || isErrorFigure ? (
          <Alert variant="danger">Failed to load performance</Alert>
        ) : (
          <>
            <Row>
              <Col as="dt" xxl={5} xl={6} lg={3} md={4}>
                {isChained(figure) ? "Chained return" : "Performance"}
              </Col>
              <Col as="dd">{formatPercent(figure?.return ?? null, 2)}</Col>
            </Row>
            <Row>
              <Col as="dt" xxl={5} xl={6} lg={3} md={4}>
                Dividends/Interest
              </Col>
              <Col as="dd">
                {formatCurrencyComponent(householdPerformance.income, 2)} (
                {formatPercent(
                  householdTotalsCurrent.aum === 0
                    ? Infinity
                    : householdPerformance.income / householdTotalsCurrent.aum,
                  2,
                )}
                )
              </Col>
            </Row>
            <Row>
              <Col as="dt" xxl={5} xl={6} lg={3} md={4}>
                Contributions
              </Col>
              <Col as="dd">
                {formatCurrencyComponent(householdPerformance.contributions, 2)}{" "}
                (
                {formatPercent(
                  householdTotalsCurrent.aum === 0
                    ? Infinity
                    : householdPerformance.contributions /
                        householdTotalsCurrent.aum,
                  2,
                )}
                )
              </Col>
            </Row>
            <Row>
              <Col as="dt" xxl={5} xl={6} lg={3} md={4}>
                Distributions
              </Col>
              <Col as="dd">
                {formatCurrencyComponent(householdPerformance.withdrawals, 2)} (
                {formatPercent(
                  householdTotalsCurrent.aum === 0
                    ? Infinity
                    : householdPerformance.withdrawals /
                        householdTotalsCurrent.aum,
                  2,
                )}
                )
              </Col>
            </Row>
          </>
        )}
      </Col>
    </Row>
  );
};

export default HouseholdPerformance;
